import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, Grid, LinearProgress, Switch } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import { useHistory, useParams } from 'react-router-dom';

import { DataAPIContext } from '../api';
import { UPDATE_BATCH } from '../api/dataProvider';
import AddDivider from '../components/AddDivider';
import FormActionButtons from '../components/FormActionButtons';
import StyledFilledTextField from '../components/StyledFilledTextField';
import { ContextStore } from '../store/contextStore';
import { asJSONPath } from '../utils';
import fileToSha1String from '../utils/file_to_sha1';
import validURL from '../utils/validators/url';
import DocumentEditor from './ActivityOntripDocuments';
import LinkEditor from './ActivityOntripLinks';
import ActivityRecommendedNearbyEditor from './ActivityOntripNearby';

const useStyles = makeStyles((theme) => ({
    root: {},
    tabButton: {
        borderRadius: '30px',
        textTransform: 'capitalize',
        fontWeight: 'normal',
    },
    dragHandle: {
        '&:hover': {
            cursor: 'pointer',
        },
    },
    dragPreview: {
        background: '#e6f2ff',
        border: '1px solid #b3d7ff',
        borderRadius: '5px',
    },
}));

const compare = (a, b) => {
    if (a.sequence < b.sequence) {
        return -1;
    }
    if (a.sequence > b.sequence) {
        return 1;
    }
    return 0;
};

const DOCUMENTS = 1;
const LINKS = 2;
const NEARBY = 3;

const ActivityOntripEditor = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const urlParams = useParams();
    const history = useHistory();
    const { hasPerm } = React.useContext(ContextStore);
    const paramsToTab = {
        links: LINKS,
        documents: DOCUMENTS,
        nearby: NEARBY,
    };
    const activeTab = paramsToTab[urlParams.subtab] ? paramsToTab[urlParams.subtab] : LINKS;
    const indexedData = (props.data || []).map((d, i) => ({ ...d, _idx: i }));
    const nearby = (props.nearby || []).map((d, i) => ({ ...d, _idx: i }));
    const links = indexedData.filter((e) => e.document.type === LINKS);
    const documents = indexedData.filter((e) => e.document.type === DOCUMENTS);
    const documentsAndLinks = [...indexedData];
    const syncActive = false;
    const { enqueueSnackbar } = useSnackbar();
    const classes = useStyles();
    const [linkDialogOpen, setLinkDialogOpen] = React.useState(false);
    const [newLinkData, setNewLinkData] = React.useState({ link: '', name: '', deep_link: false });
    const [validUrlError, setValidUrlError] = React.useState(false);

    React.useEffect(() => {
        if (paramsToTab[urlParams.subtab] === void 0) {
        }
    }, [activeTab, urlParams.id, urlParams.tab, urlParams.subtab]);

    const handleTabChange = (newTab) => {
        const match = Object.keys(paramsToTab).filter((x) => paramsToTab[x] === newTab);
        if (match && match.length === 1) {
            history.push(`/activity/${urlParams.id}/ontrip/${match[0]}`);
        }
    };

    let maxseq = links.concat(documents).reduce((a, e) => (+e.sequence ? Math.max(a, e.sequence) : a), 0);

    const onChange = (l, d, combinedLD) => {
        let seq = 0;
        const update = [...l.map((x) => ({ ...x, sequence: seq++ })), ...d.map((x) => ({ ...x, sequence: seq++ }))];
        let cseq = 0;
        if (combinedLD) {
            props.onChange && props.onChange([...combinedLD.map((x) => ({ ...x, sequence: cseq++ }))]);
        } else {
            props.onChange && props.onChange(update);
        }
    };

    const saveOrder = (update) => {
        const sorted = update
            ? update
            : (props.data || [])
                  .filter((x) => !!x.id)
                  .slice()
                  .sort((a, b) => a.sequence - b.sequence);
        const params = {
            urlSuffix: 'reorder/',
            data: {
                documents: sorted.map((x) => x.id),
                ...props.resourceMeta,
            },
        };
        return dataAPI(UPDATE_BATCH, props.resource, params).then((res) => {
            enqueueSnackbar('New order saved', { variant: 'success', autoHideDuration: 1000 });
        });
    };


    const handleAddLink = () => {
        setLinkDialogOpen(true);
    };

    const handleLinkSave = () => {
        const newDocumentsAndLinks = [
            ...documentsAndLinks,
            {
                id: null,
                sequence: maxseq + 1,
                document: {
                    id: null,
                    link: newLinkData.link,
                    name: newLinkData.name,
                    deep_link: newLinkData.deep_link,
                    type: LINKS,
                },
            },
        ];
        setNewLinkData({ link: '', name: '', deep_link: false });
        setLinkDialogOpen(false);
        onChange([], documents, newDocumentsAndLinks);
    };

    const handleLinkCancel = () => {
        setNewLinkData({ link: '', name: '', deep_link: false });
        setLinkDialogOpen(false);
        setValidUrlError(false);
    };

    const handleLinkInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'deep_link') {
            setNewLinkData((prevData) => ({ ...prevData, [name]: e.target.checked }));
            return;
        }
        setNewLinkData((prevData) => ({ ...prevData, [name]: value }));
    };

    const onLinkDeleted = (idx) => {
        const newDocumentsAndLinks = documentsAndLinks.filter((e, i) => i !== idx);
        onChange([], documents, newDocumentsAndLinks);
    };
    const handleFileSelect = async (acceptedFiles) => {
        let hash_string = await fileToSha1String(acceptedFiles[0]);
        if (documentsAndLinks.filter((doc) => doc?.document?.content_hash === hash_string).length > 0) {
            enqueueSnackbar('Duplicate file found, please check existing attachments', { variant: 'default', autoHideDuration: 3000 });
        } else {
            const newDocument = {
                id: null,
                sequence: maxseq + 1,
                state: 0, // 0 - new
                document: {
                    id: null,
                    link: null,
                    name: acceptedFiles[0].name,
                    file: acceptedFiles[0],
                    type: DOCUMENTS,
                },
            };
            onChange([], documents, [...documentsAndLinks, newDocument]);
        }
    };
    const dz = useDropzone({
        noClick: true,
        noKeyboard: true,
        maxFiles: 1,
        onDrop: handleFileSelect,
        multiple: false,
    });
    const onDocumentAdd = async () => {
        console.log("XYZ onDCO", dz)


        dz.open();
    };

    const onNearbyAdd = (ev) => {
        props.onNearbyPathChange && props.onNearbyPathChange(asJSONPath(`children[${nearby.length}]`, { activity: null, id: null, name: '' }));
    };

    const onNearbyChange = (data, idx) => {
        props.onNearbyPathChange && props.onNearbyPathChange(asJSONPath(`children[${idx}]`, data));
    };

    const onNearbyDelete = (idx) => {
        props.onNearbyChange && props.onNearbyChange(nearby.filter((e, i) => i !== idx));
    };

    const onDocumentDeleted = (idx) => {
        const newDocumentsAndLinks = documentsAndLinks.filter((e, i) => i !== idx);
        onChange(links, [], newDocumentsAndLinks);
    };

    const swapDocumentsOrLinks = (i, j) => {
        const newDocumentsAndLinks = documentsAndLinks.map((item, idx) => {
            if (idx === i) {
                return documentsAndLinks[j];
            } else if (idx === j) {
                return documentsAndLinks[i];
            } else {
                return item;
            }
        });

        const newLinks = newDocumentsAndLinks.filter((e) => e.document.type === LINKS);
        const newDocs = newDocumentsAndLinks.filter((e) => e.document.type === DOCUMENTS);

        onChange(newDocs, newLinks, newDocumentsAndLinks);
        saveOrder(newDocumentsAndLinks);
    };

    const swapNearby = (i, j) => {
        const newNearby = nearby.map((item, idx) => {
            delete item._idx;
            if (idx === i) {
                return nearby[j];
            } else if (idx === j) {
                return nearby[i];
            } else {
                return item;
            }
        });
        props.onNearbyChange && props.onNearbyChange(newNearby);
    };

    const onPathChange = (data, idx) => {
        props.onPathChange && props.onPathChange(asJSONPath(`documents[${idx}]`, data));
    };

    const handleURLOnBlur = () => {
        if (validURL(newLinkData.link)) {
            setValidUrlError(false);
        } else {
            setValidUrlError(true);
        }
    };

    return (
        <div className={classes.root}>
            <Grid container direction="row" spacing={2}>
                <Grid item xs={6} sm={6} md={4} lg={3}>
                    <Button style={{ width: !props.isSmallScreen ? '250px' : '100%' }} color="primary" size="small" className={classes.tabButton} variant={activeTab === LINKS || activeTab === DOCUMENTS ? 'contained' : 'outlined'} onClick={() => handleTabChange(LINKS)}>
                        Documents & Links
                    </Button>
                </Grid>
                <Grid item xs={6} sm={6} md={4} lg={3}>
                    <Button style={{ width: !props.isSmallScreen ? '250px' : '100%' }} color="primary" size="small" className={classes.tabButton} variant={activeTab === NEARBY ? 'contained' : 'outlined'} onClick={() => handleTabChange(NEARBY)}>
                        Recommended nearby
                    </Button>
                </Grid>
                {activeTab === LINKS || activeTab === DOCUMENTS ? (
                    <>
                        {documentsAndLinks.sort(compare).map((e, idx) => {
                            if (e.document.type === LINKS) {
                                return (
                                    <Grid key={`${idx}-${e.id}-${e.document.link}-${e.document.name}`} item xs={12}>
                                        <LinkEditor
                                            data={e}
                                            idx={idx}
                                            resource={props.resource}
                                            resourceMeta={props.resourceMeta}
                                            onDeleted={onLinkDeleted}
                                            onChange={onPathChange}
                                            swapItems={swapDocumentsOrLinks}
                                            saveOrder={saveOrder}
                                            documentsAndLinks={documentsAndLinks}
                                            isSmallScreen={props.isSmallScreen}
                                        />
                                        {idx < documentsAndLinks.length - 1 ? <Divider /> : null}
                                    </Grid>
                                );
                            }
                            if (e.document.type === DOCUMENTS) {
                                return (
                                    <Grid key={`${idx}-${e.id}-${e.document.link}-${e.document.name}`} item xs={12}>
                                        <DocumentEditor
                                            data={e}
                                            idx={idx}
                                            resource={props.resource}
                                            resourceMeta={props.resourceMeta}
                                            onDeleted={onDocumentDeleted}
                                            onChange={onPathChange}
                                            syncActive={syncActive}
                                            swapItems={swapDocumentsOrLinks}
                                            saveOrder={saveOrder}
                                            documentsAndLinks={documentsAndLinks}
                                            isSmallScreen={props.isSmallScreen}
                                        />
                                        {idx < documentsAndLinks.length - 1 ? <Divider /> : null}
                                    </Grid>
                                );
                            } else {
                                return <></>;
                            }
                        })}
                        <Grid item xs={12} container justifyContent="space-between">
                            <Grid item xs={6} container justifyContent="flex-start">
                                <Button disabled={!hasPerm('activity', 'write')} variant="outlined" color="primary" onClick={handleAddLink} >
                                    <AddIcon fontSize="small"/>&nbsp;Add link
                                </Button>
                            </Grid>
                            <Grid item xs={6} container justifyContent="flex-end">
                                <input {...dz.getInputProps()} />

                                <Button variant="outlined" disabled={!hasPerm('activity', 'write')} color="primary" onClick={onDocumentAdd}>
                                    <AddIcon fontSize="small"/>&nbsp;Add document
                                </Button>
                            </Grid>
                        </Grid>
                    </>
                ) : null}
                {activeTab === NEARBY ? (
                    <React.Fragment>
                        <Grid item xs={1}></Grid>
                        <Grid item xs={11} style={{ visibility: syncActive ? 'visible' : 'hidden' }}>
                            <Box my={2}>
                                <LinearProgress color="primary" />
                            </Box>
                        </Grid>
                        {nearby.map((e, idx) => (
                            <Grid key={`${e.name}-${e.id}`} item xs={12}>
                                <ActivityRecommendedNearbyEditor
                                    data={e}
                                    idx={idx}
                                    resourceMeta={props.resourceMeta}
                                    onChange={onNearbyChange}
                                    onDelete={onNearbyDelete}
                                    syncActive={syncActive}
                                    swapItems={swapNearby}
                                    disabled={!hasPerm('activity', 'write')}
                                />
                                {idx < documents.length - 1 ? <Divider /> : null}
                            </Grid>
                        ))}
                        <Grid item xs={12} container direction="row" alignItems="center">
                            <AddDivider onAdd={onNearbyAdd} />
                        </Grid>
                    </React.Fragment>
                ) : null}
            </Grid>

            <Dialog open={linkDialogOpen} onClose={handleLinkCancel} PaperProps={{
                style: {
                    width: '300px',
                    maxWidth: '100%',
                    margin: 'auto'
                }
            }}>
                <DialogTitle>Add New Link</DialogTitle>
                <DialogContent >
                    <Grid item xs={12} mb={2} >
                        <StyledFilledTextField
                            disabled={props.disabled}
                            label="Display name"
                            name="name"
                            value={newLinkData.name}
                            variant="filled"
                            size="small"
                            onChange={handleLinkInputChange}
                            fullWidth
                            autoFocus
                        />
                    </Grid>
                    <Grid item xs={12} mb={2} >
                        <StyledFilledTextField
                            disabled={props.disabled}
                            label="Link"
                            name="link"
                            value={newLinkData.link}
                            variant="filled"
                            size="small"
                            onChange={handleLinkInputChange}
                            fullWidth
                            onBlur={handleURLOnBlur}
                            error={validUrlError}
                            helperText={validUrlError && 'Please enter a valid URL.'}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel disabled={props.disabled} label="Deep link" control={<Switch name="deep_link" color="primary" disabled={props.disabled} checked={newLinkData.deep_link} onChange={handleLinkInputChange} />} />
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    {/* Not "Save": this only adds the row; the row's own SAVE creates it on the server. */}
                    <FormActionButtons
                        mode="edit"
                        size="medium"
                        submitLabel="Add"
                        submitIcon={null}
                        onSubmit={handleLinkSave}
                        submitDisabled={!newLinkData.name || !newLinkData.link || validUrlError}
                        onCancel={handleLinkCancel}
                    />
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ActivityOntripEditor;
